import { ComponentStore, OnStoreInit, tapResponse } from '@ngrx/component-store';
import { exhaustMap, first, pipe, tap } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { handleError } from '../../../shared/utils/handle-error';
import {
  WorkspaceDataService,
  Workspace,
  WorkspaceResponse,
} from '../features/create-workspace/services/workspace-data.service';
import { SelectedWorkspaceStore } from '../../../stores/selected-workspace.store';
import { ActivatedRoute, EventType, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { SaveLocalService } from '../../auth/services/save-local.service';
import { TokenService } from '../../auth/services/token.service';
import { ProjectFromList } from '../features/create-workspace/services/workspace-api.service';
import { init } from '@sentry/angular';
import { error } from '@angular/compiler-cli/src/transformers/util';

export type WorkspaceStoreState = {
  isLoading: boolean;
  isInited: boolean;
  workspaces: {
    my: ProjectFromList[];
    others: ProjectFromList[];
  };
};

@Injectable()
export class WorkspaceStore extends ComponentStore<WorkspaceStoreState> implements OnStoreInit {
  public stateVm$ = this.select((state) => state, { debounce: true });

  constructor(
    private workspaceService: WorkspaceDataService,
    private token: TokenService,
  ) {
    super({
      isLoading: true,
      isInited: false,
      workspaces: {
        my: [],
        others: [],
      },
    });
  }

  public clear() {
    this.patchState({
      isLoading: false,
      isInited: false,
      workspaces: {
        my: [],
        others: [],
      },
    });
  }

  ngrxOnStoreInit() {
    this.init();
  }

  public init = this.effect<void>(
    pipe(
      filter(() => !!this.token.getToken()),
      tap(() => this.patchState({ isLoading: true })),
      exhaustMap(() =>
        this.workspaceService.getWorkspaces().pipe(
          tapResponse(
            (res: any) => {
              if (!res) {
                this.patchState({ 
                  isLoading: false, 
                  isInited: false,
                  workspaces: { my: [], others: [] }
                });
                this.handleError(new Error('Empty response from server'));
                return;
              }

              const myProjects = Array.isArray(res.my) ? res.my : [];
              const otherProjects = Array.isArray(res.others) ? res.others : [];

              console.log(`Loaded ${myProjects.length} my projects, ${otherProjects.length} other projects`);

              this.patchState({
                isInited: true,
                workspaces: {
                  my: myProjects,
                  others: otherProjects,
                },
                isLoading: false,
              });
            },
            (err) => {
              this.patchState({
                isLoading: false,
                isInited: false,
                workspaces: { my: [], others: [] },
              });

              if (err instanceof HttpErrorResponse && err.status === 401) {
                return;
              }

              const normalizedError =
                err instanceof Error ? err : new Error('Error while loading workspaces list');
              this.handleError(normalizedError);
            },
          ),
        ),
      ),
    ),
  );

  public createWorkspace = this.effect<string>((name$) => {
    return name$.pipe(
      exhaustMap((name) => {
        this.workspaceService.setSpaceData(name, 'personal');
        return this.workspaceService.createWorkspace().pipe(
          tapResponse(
            () => {
              this.init();
            },
            () => {
              this.handleError(new Error('Error while creating workspace'));
            },
          ),
        );
      }),
    );
  });

  public refreshProjects() {
    this.init();
  }

  private handleError(error: Error) {
    handleError(error, { source: 'workspace-companies', operation: 'companies-management' });
  }
}
